import { ParsedQuery } from 'query-string'

import { FILTERS } from '@components/music/filter/utils/constants'
import {
  facetTermDisplayText,
  getAggregationCategory,
  isValidFilter,
} from '@components/music/filter/utils/helpers'
import { SlugFilter } from '@providers/types/api-responses'

import { TagsType } from './types'

/**
 * Converts URL params coming from the URL to UI readable tag data. Also parse the slug filter if it is present
 *
 * @example
 * IN: instrument_names: ['Trumpet', 'Alto Sax', 'Bass Guitar']
 * OUT: {Trumpet: 'instrument_names', Alto Sax: 'instrument_names', Bass Guitar: 'instrument_names'}
 */
export const convertParamsToTags = (params: ParsedQuery, slugFilter?: SlugFilter): TagsType => {
  const dupParams = { ...params }

  // If a slug filter is present (such as `/music/piano-vocal`), add to set of params
  if (slugFilter) {
    const { category, value } = slugFilter
    const currFilters = dupParams[category]
    if (currFilters) {
      if (typeof currFilters === 'string') dupParams[category] = [currFilters, value]
      else dupParams[category] = [...currFilters, value]
    } else {
      dupParams[category] = [value]
    }
  }

  return Object.keys(dupParams)
    .filter((key) => isValidFilter(key, true))
    .reduce((currTags, key) => {
      const dupCurrTags: TagsType = { ...currTags }

      const valueOfParamCategory = dupParams[key]
      if (typeof valueOfParamCategory === 'string') {
        dupCurrTags[valueOfParamCategory] = key
      } else {
        valueOfParamCategory?.forEach((v: string | null) => {
          if (v) {
            dupCurrTags[v] = key
          }
        })
      }
      return dupCurrTags
    }, {} as TagsType)
}

/**
 * Gets the display name for the given tag and category
 */
export const getTagDisplayName = (tag: string, category: string): string => {
  // The display name for the term category is just the tag content
  if (category === 'term') return tag

  // Facet translations are based on the ES aggregation categories, parse the correct filter
  // name from the given category
  const filter = getAggregationCategory(category)
  return facetTermDisplayText(filter, tag, FILTERS[filter]?.translationPrefix)
}
